import React, { useEffect } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { makeStyles, Theme } from "@material-ui/core/styles";
import {
  KButton, KCheckbox, KDialog,
  KIcon,
  KMenuDropDown, KTextInput
} from "@kyburz/kdesign-system";
import { useDispatch } from "react-redux";
import useSetState from "../../hooks/useSetState";
import BulkUpload from "../../components/bulkUpload/BulkUpload";
import { iotKeyDeviceElements } from "../../utils/bulkUploadElements";

import {
  deleteIoTDevice,
  getIoTKeyDevices,
  importIotKeyDevices,
} from "../../apiCalls/config/iotKeyDevices";
import { IotKeyDevice } from "../../interfaces/IoKeyDeviceInterface";
import { formatDate } from "../../utils/timeUtils";
import { setAlert } from "../../redux/actions/actions";
import KInfiniteTable from "../../components/table/KInfiniteTable";
import { sendIotCommand } from "../../apiCalls/config/devices";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      filterTable={props.filterTable}
      loading={props.loading}
      sortTable={props.sortTable}
      manualRowSelectedKey={props.manualRowSelectedKey}
      onControlledSelect={props.onControlledSelect}
      onControlledSelectAll={props.onControlledSelectAll}
      autoResetSelectedRows={props.autoResetSelectedRows}
      hasFilter
      manualFilters
      hasStickyColumns={true}
      manualSortBy
      selectionColumnProps={{ sticky: "left" }}
    />
  ),
  (oldProps, newProps) => {
    return (
      oldProps.loading === newProps.loading && oldProps.data === newProps.data
    );
  },
);

const useStyles = makeStyles((theme: Theme) => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-end",
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
  iconContainer: {
    cursor: "pointer",
    display: "flex",
    flex: 1,
    justifyContent: "center",
  },
  loadedText: {
    fontSize: 12,
    flexGrow: 1,
    color: theme.palette.grey[500],
    marginBottom: 0,
  },
  dropDownContainer: {
    marginBottom: 10,
  },
  optionsContainer: {
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: "10px",
  },
  reloadButton: {
    marginLeft: "auto",
    marginRight: 16,
    border: "1px solid #E0E0E0",
  },
}));

const IoTKeyDeviceList: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const firstUpdate = React.useRef(true);
  const { CancelToken } = axios;
  const dispatch = useDispatch();

  const [
    {
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      deleteModalOpen,
      deviceToDelete,
      sendCommandModalOpen,
      command,
      unsigned,
      reload,
      cancelTokenSource,
      showBulkUploadModal,
      filterBy,
      sortBy,
      loading,
      isAllSelected,
      selectedDeviceIds,
      selectedVehicleIds,
    },
    setState,
  ] = useSetState({
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [] as IotKeyDevice[],
    deleteModalOpen: false,
    deviceToDelete: "",
    sendCommandModalOpen: false,
    command: "",
    unsigned: false,
    reload: false,
    cancelTokenSource: null,
    showBulkUploadModal: false,
    filterBy: "",
    sortBy: "",
    loading: true,
    isAllSelected: false,
    selectedDeviceIds: [],
    selectedVehicleIds: [],
  });

  const handleReload = () => {
    setState({ reload: true });
  };

  const handleDelete = async () => {
    setState({ deleteModalOpen: false, deviceToDelete: "", query: "" });
    const res = await deleteIoTDevice(deviceToDelete._id);
    if (res.data) {
      dispatch(setAlert(`Device ${deviceToDelete.iot_key_id} deleted`, "success"));
      handleReload();
    } else {
      dispatch(setAlert(res.detail, "error"));
    }
  };

  const cancelPopup = () => {
    setState({deleteModalOpen: !deleteModalOpen, deviceToDelete: ""})
  };

  const cancelSendCommandPopup = () => {
    setState({sendCommandModalOpen: !sendCommandModalOpen})
  };


  const loadDevicesPage = (page, startEmpty = true) => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();
    setState({
      isNextPageLoading: true,
      cancelTokenSource: source,
      loading: true,
    });
    getIoTKeyDevices(page, source.token, filterBy, sortBy)
      .then((res) => {
        if (res.data?.data) {
          const previousArray = startEmpty ? [] : items;
          setState({
            items: [...previousArray].concat(...res?.data?.data),
            isNextPageLoading: false,
            hasNextPage: res.data?.has_next_page,
            pagesLoaded: page,
            cancelToken: null,
            loading: false,
          });
        }
      })
      .catch((e) => {
        setState({
          isNextPageLoading: false,
          pagesLoaded: page,
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const loadNextPage = () => {
    if (items.length > 0) loadDevicesPage(pagesLoaded + 1, false);
  };

  const getFilename = (url: string) => {
    if (url && url.startsWith("https://fleetiotkey.s3.amazonaws.com")) {
      return new URL(url).pathname.replace(/^.*[\\/]/, "");
    }
    return "No file";
  };

  const getVehicleId = (id: string) => {
    if (id && id !== "") {
      return id;
    }
    return "No vehicle";
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "IoT Key ID",
        accessor: "iot_key_id",
        width: 300,
        defaultCanSort: true,
      },
      {
        Header: "ID",
        accessor: "_id",
        width: 300,
        defaultCanSort: false,
      },
      {
        Header: "Customer",
        accessor: "company_id",
        defaultCanSort: true,
      },
      {
        Header: "Created",
        accessor: (row) => formatDate(row.created),
        width: 150,
        defaultCanSort: true,
        disableFilters: true,
      },
      {
        Header: "Modified",
        accessor: (row) => formatDate(row.modified),
        width: 150,
        disableFilters: true,
        defaultCanSort: true,
      },
      {
        Header: "Firmware Version",
        accessor: "firmware_version",
        width: 200,
      },
      {
        Header: "Hardware Version",
        accessor: "hardware_version",
        width: 200,
        defaultCanSort: true,
      },
      {
        Header: "Vehicle ID",
        accessor: (row) => getVehicleId(row.vehicle_id),
        width: 300,
        defaultCanSort: true,
      },
      {
        Header: "Firmware Config File",
        accessor: (row) => getFilename(row.firmware_config_file),
        defaultCanSort: false,
        width: 250,
        disableFilters: true,
      },
      {
        Header: "Actions",
        Cell: (row) => {
          return (
            <div className={classes.optionsContainer}>
              <div
                className={classes.iconContainer}
                onClick={() =>
                  history.push(
                    `/config/iot_key_devices/device/${row.row.original._id}`,
                  )
                }
              >
                <KIcon icon="eye" color="#383F51" size={20} />
              </div>
              <div
                className={classes.iconContainer}
                onClick={() => {
                  setState({
                    deleteModalOpen: true,
                    deviceToDelete: row.row.original,
                  })
                }
                }
              >
                <KIcon icon="trash-2" color="#383F51" size={20} />
              </div>
            </div>
          );
        },
        width: 100,
        sticky: "right",
      },
    ],
    [],
  );

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => cancelPopup()}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the iot device <b>{deviceToDelete.iot_key_id}</b>?
      </p>
    </KDialog>
  );

  const toggleSendCommandPopup = () => {
    setState({ sendCommandModalOpen: !sendCommandModalOpen });
  };

  const sendCommandSuccess = () => {
    setState({ sendCommandModalOpen: false, reload: true });
  };

  const handleSendCommand = () => {
    sendIotCommand(selectedVehicleIds, command, unsigned).then((res: any) => {
      if (res.data) {
        let errorEntries : string[] = [];
        if (res.data.errors_by_uuid) {
          const errors = res.data.errors_by_uuid;
          const errorEntriesUuid = (Object.entries(errors).filter(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([_, value]) => value !== "ok"
          ));
          errorEntries = errorEntries.concat(errorEntriesUuid.map( ([key, value]) => `${key}: ${value}` ));
        }
        if (res.data.errors_by_imei) {
          const errors = res.data.errors_by_imei;
          const errorEntriesImei = (Object.entries(errors).filter(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([_, value]) => value !== "ok"
          ));
          errorEntries = errorEntries.concat(errorEntriesImei.map( ([key, value]) => `${key}: ${value}` ));
        }

        if (errorEntries.length > 0) {
          if (errorEntries.length / 2 >= selectedVehicleIds.length) {
            dispatch(setAlert(`Command sent, all with errors: ${errorEntries.join(", ")}`, "error"));
            return;
          }
          dispatch(setAlert(`Command sent, some with errors: ${errorEntries.join(", ")}`, "warning"));
          sendCommandSuccess();
          return;
        }
        dispatch(setAlert("Command sent successfully", "success"));
        sendCommandSuccess();
      } else {
        dispatch(setAlert("Failed to send command", "error"));
      }
    });
  };

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const handleUnsigned = (e) => setState({ unsigned: e.target.checked });

  const sendCommandPopup = (
    <KDialog
      size="xs"
      title={`Send command to ${selectedDeviceIds.length} devices`}
      open={sendCommandModalOpen}
      primaryCTAonClick={handleSendCommand}
      primaryCTAColor="danger"
      primaryCTAText="Send"
      secondaryCTAonClick={() => cancelSendCommandPopup()}
      secondaryCTAText="Cancel"
    >
      <KTextInput
        placeholder="Type IoT Key Command"
        width="100%"
        onChange={handleChange}
        name="command"
        value={command}
      />
      <KCheckbox
        name="unsigned"
        handleChange={handleUnsigned}
        label="Unsigned"
        checked={unsigned}
      />
    </KDialog>
  );

  const deviceUploadBody = [
    "company_id",
    "iot_key_id",
    "firmware_version",
    "hardware_version",
    "firmware_config_file",
  ];

  const toggleBulkUpload = () => {
    setState({ showBulkUploadModal: !showBulkUploadModal });
    if (showBulkUploadModal) {
      handleReload();
    }
  };

  const importBulk = (request): any => {
    return importIotKeyDevices(request);
  };

  const bulkUploadPopup = (
    <BulkUpload
      open={showBulkUploadModal}
      toggleBulkUpload={toggleBulkUpload}
      elements={iotKeyDeviceElements}
      importFunction={importBulk}
      body={deviceUploadBody}
    />
  );

  const filterTable = (values: { id: string; value: string }[]): void => {
    const parsedFilter: string = values
      .map((x) => `${x.id}:regex:${x.value}`)
      .join(";");
    setState({ filterBy: `filters=${parsedFilter}` });
  };

  const sortTable = (values: { id: string; desc: boolean }[]): void => {
    const transformIdToSort = (value: string, desc: boolean): string => {
      const order = desc ? "desc" : "asc";
      return `order_by=${value.toLowerCase()}&order=${order}`;
    };
    const parsedSort: string = values
      .map((x) => `${transformIdToSort(x.id, x.desc)}`)
      .join(",");
    setState({ sortBy: parsedSort });
  };

  const onControlledSelectAll = (data, checked) => {
    setState({
      isAllSelected: checked,
      selectedDeviceIds: checked ? data.map((item) => item?._id) : [],
      selectedVehicleIds: checked ? data.map((item) => item?.vehicle_id) : [],
    });
  };

  const onControlledSelect = (row, checked) => {
    const selectedDeviceIdsCopy = [...selectedDeviceIds];
    const selectedVehicleIdsCopy = [...selectedVehicleIds];
    const deviceId = row.original._id;
    const vehicleId = row.original.vehicle_id;
    let isAll = isAllSelected;
    if (checked) {
      selectedDeviceIdsCopy.push(deviceId);
      selectedVehicleIdsCopy.push(vehicleId);
    } else {
      const index = selectedDeviceIdsCopy.indexOf(deviceId);
      selectedDeviceIdsCopy.splice(index, 1);
      selectedVehicleIdsCopy.splice(index, 1);
      isAll = false;
    }
    setState({
      selectedDeviceIds: selectedDeviceIdsCopy,
      selectedVehicleIds: selectedVehicleIdsCopy,
      isAllSelected: isAll,
    });
  };

  const handledItems = React.useMemo(
    () =>
      items.map((item) => ({
        ...item,
        selected: isAllSelected || selectedDeviceIds.includes(item?._id),
      })),
    [items, selectedDeviceIds],
  );

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadDevicesPage(1, true);
    }
  }, [reload]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    loadDevicesPage(1, true);
  }, [filterBy, sortBy]);

  return (
    <>
      <div className={classes.ctaContainer}>
        <p className={classes.loadedText}>
          {loading
            ? "Loading IoT key devices..."
            : isAllSelected
            ? "All devices selected"
            : `Selected ${selectedDeviceIds.length} / ${items?.length} loaded devices`}
        </p>
        <KButton
          theme="primary"
          children="Reload Devices"
          onClick={() => setState({ reload: true })}
          className={classes.reloadButton}
        />
        <div className={classes.dropDownContainer}>
          <KMenuDropDown
            menuItems={[
              {
                icon: "plus",
                title: "New IoT Key Device",
                action: () => history.push("/config/iot_key_devices/create"),
              },
              {
                icon: "upload",
                title: "Import CSV",
                action: toggleBulkUpload,
              },
              {
                icon: "terminal",
                title: "Send command",
                action: toggleSendCommandPopup,
              },
            ]}
          />
        </div>
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          data={handledItems}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columns={columns}
          filterTable={filterTable}
          loading={loading}
          sortTable={sortTable}
          manualRowSelectedKey="selected"
          onControlledSelect={onControlledSelect}
          onControlledSelectAll={onControlledSelectAll}
          autoResetSelectedRows={true}
        />
      </div>
      {deletePopup}
      {bulkUploadPopup}
      {sendCommandPopup}
    </>
  );
};

export default IoTKeyDeviceList;
